import { useState } from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCompany } from '../context/CompanyContext';
import { cn } from '@/lib/utils';

export function CompanySelector() {
  const { currentCompany, availableCompanies, switchCompany, isLoading } = useCompany();
  const [open, setOpen] = useState(false);

  const handleSelect = async (companyId: string) => {
    if (companyId === currentCompany?.id) {
      setOpen(false);
      return;
    }

    try {
      await switchCompany(companyId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!currentCompany || availableCompanies.length === 0) {
    return null;
  }

  // If only one company, show it without dropdown but with visual prominence
  if (availableCompanies.length === 1) {
    return (
      <div className="px-3 py-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 shadow-md hover:shadow-lg transition-all duration-200">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-primary/20 border border-primary/30">
            <Building2 className="h-4 w-4 text-primary" />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Company</span>
            <span className="font-bold text-sm text-foreground leading-tight">{currentCompany.name}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30 shadow-md hover:shadow-lg transition-all duration-200">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="w-[200px] justify-between hover:bg-primary/15 h-auto py-1 px-2 transition-all duration-200"
          >
            <div className="flex items-center gap-2.5 truncate">
              <div className="p-1.5 rounded-md bg-primary/20 border border-primary/30 shrink-0">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Company</span>
                <span className="truncate font-bold text-sm text-foreground leading-tight">{currentCompany.name}</span>
              </div>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="end">
          <Command>
            <CommandInput placeholder="Search company..." className="h-9" />
            <CommandEmpty>No company found.</CommandEmpty>
            <CommandGroup>
              {availableCompanies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={company.id}
                  onSelect={() => handleSelect(company.id)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      currentCompany.id === company.id ? 'opacity-100 text-primary' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">{company.name}</span>
                      <span className="text-xs text-muted-foreground">Code: {company.code}</span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
